/** A escala do mostrador: as cinco faixas de saúde impressas inteiras, com dois ponteiros
 * (hoje e a leitura de referência). Um instrumento imprime a escala toda, não só o trecho
 * onde a agulha está.
 */

import { dayMonth, short } from "../lib/format";
import { useMeasuredWidth } from "./Tape";

import { zoneClass } from "./ui";
import type { HealthLevel, ZoneColor } from "../lib/types";

/** As faixas impressas na régua são as do usuário, não um padrão cravado aqui.
 *
 * Estavam fixas em 0 · 1.000 · 3.000 · 10.000. Desde que as faixas viraram editáveis, um
 * saldo de R$ 4.000 podia cair na faixa verde impressa e o motor devolver âmbar para o
 * mesmo número, no mesmo painel: a cor discordando de si mesma. Num app onde cor tem
 * significado, isso é pior do que não ter cor. */
export function faixasDe(levels?: HealthLevel[]) {
  if (!levels?.length) {
    return {
      bands: [
        { id: "crit", from: -Infinity, to: 0 },
        { id: "urg", from: 0, to: 1000 },
        { id: "att", from: 1000, to: 3000 },
        { id: "ok", from: 3000, to: 10000 },
        { id: "abn", from: 10000, to: Infinity },
      ],
      ticks: [0, 1000, 3000, 10000],
    };
  }
  const bands = [...levels]
    .map((l) => ({
      // zoneClass traduz a cor da API (rose, amber, emerald…) para a classe do CSS.
      // Comparar direto contra "crit"/"att"/"ok" nunca casava, e a régua caía no padrão
      // antigo mesmo depois de o usuário editar as faixas.
      id: zoneClass(l.color as ZoneColor),
      from: l.min ?? -Infinity,
      to: l.max ?? Infinity,
    }))
    .sort((a, b) => a.from - b.from);
  const ticks = bands.map((b) => b.from).filter((v) => Number.isFinite(v));
  return { bands, ticks };
}

export function Scale({
  now,
  other,
  otherDate,
  levels,
}: {
  now: number;
  other: number;
  otherDate: string;
  levels?: HealthLevel[];
}) {
  const { ref, width } = useMeasuredWidth();
  return (
    <div className="tapebox" ref={ref}>
      {width > 0 && (
        <ScaleSvg
          now={now}
          other={other}
          otherDate={otherDate}
          levels={levels}
          W={Math.max(280, width)}
        />
      )}
    </div>
  );
}

function ScaleSvg({
  now,
  other,
  otherDate,
  levels,
  W,
}: {
  now: number;
  other: number;
  otherDate: string;
  levels?: HealthLevel[];
  W: number;
}) {
  const { bands: BANDS, ticks: TICKS } = faixasDe(levels);
  const H = 56;
  const L = 4;
  const R = 4;
  const top = 28;
  const h = 12;

  const worst = Math.min(now, other);
  const lo = Math.min(-1000, Math.floor(worst / 500) * 500 - 500);
  const hi = Math.max(12000, Math.ceil(Math.max(now, other) / 1000) * 1000 + 1000);
  const x = (v: number) => L + ((Math.min(hi, Math.max(lo, v)) - lo) / (hi - lo)) * (W - L - R);

  // Os dois rótulos se escreviam um por cima do outro sempre que hoje e a referência
  // ficavam próximos — que é o caso NORMAL, não a exceção. Saía "3,5k · 30/09hoje 5,0k".
  // Quando não cabem lado a lado, um sobe uma linha em vez de disputar o mesmo espaço.
  const pxNow = x(now);
  const pxOther = x(other);
  const colidem = Math.abs(pxNow - pxOther) < 96;

  const pointer = (v: number, label: string, min: boolean, acima: boolean) => {
    const px = x(v);
    const anchor = px < 110 ? "start" : px > W - 110 ? "end" : "middle";
    const tx = anchor === "start" ? px - 6 : anchor === "end" ? px + 6 : px;
    return (
      <g key={label}>
        <path className={`ptr ${min ? "min" : ""}`} d={`M${px} ${top - 2} l-5 -8 h10 z`} />
        <text className="p" x={tx} y={acima ? top - 24 : top - 12} textAnchor={anchor}>
          {label}
        </text>
      </g>
    );
  };

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      role="img"
      aria-label="Escala de saúde do saldo"
    >
      {BANDS.map((b) => {
        const from = Math.max(lo, b.from === -Infinity ? lo : b.from);
        const to = Math.min(hi, b.to === Infinity ? hi : b.to);
        if (to <= from) return null;
        return (
          <rect
            key={b.id}
            x={x(from)}
            y={top}
            width={x(to) - x(from)}
            height={h}
            fill={`var(--z-${b.id})`}
            opacity=".85"
          />
        );
      })}

      {TICKS.filter((v) => v > lo && v <= hi).map((v) => (
        <g key={v}>
          <line className="tick" x1={x(v)} x2={x(v)} y1={top - 3} y2={top + h + 3} />
          <text x={x(v)} y={H - 4} textAnchor="middle">
            {v >= 1000 ? `${v / 1000}k` : v}
          </text>
        </g>
      ))}

      {pointer(other, `${short(other)} · ${dayMonth(otherDate)}`, other <= now, colidem)}
      {pointer(now, `hoje ${short(now)}`, now < other, false)}
    </svg>
  );
}
